import { ReactComponent as Close16Icon } from 'assets/image/svg/close16.svg';
import { Popover } from 'flowbite-react';
import moment, { Moment } from 'moment';
import { Dispatch, SetStateAction, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineCalendar, HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi';
import { ActivityLogParams } from 'src/api/activityLogsApi';
import InputField from 'src/components/admin/form/inputField';
import Calendar from 'src/components/HomeFilter/Calendar';
import SlideUpModal from 'src/components/SlideUpModal';
import { DATE_FORMAT } from 'src/utils/constants';
import { queryFilter } from 'src/utils/proptypes';

type FilterModalProps = {
   isOpen: boolean;
   setIsOpen: Dispatch<SetStateAction<boolean>>;
   filters: queryFilter & ActivityLogParams;
   setFilters: Dispatch<SetStateAction<queryFilter & ActivityLogParams>>;
   openCalendar: boolean;
   setOpenCalendar: Dispatch<SetStateAction<boolean>>;
   selectedMonth: Moment;
   setSelectedMonth: Dispatch<SetStateAction<Moment>>;
   dateFilter: moment.MomentInput[];
   setDateFilter: Dispatch<SetStateAction<moment.MomentInput[]>>;
   isFetching: boolean;
};

const FilterModal = ({
   isOpen,
   setIsOpen,
   filters,
   setFilters,
   openCalendar,
   setOpenCalendar,
   selectedMonth,
   setSelectedMonth,
   dateFilter,
   setDateFilter,
   isFetching,
}: FilterModalProps) => {
   const titleId = useId();
   const [t] = useTranslation();

   return (
      <SlideUpModal {...{ isOpen, setIsOpen }} titleId={titleId} drag className="m-2.5 w-[calc(100%-20px)]">
         <div className="flex flex-col gap-3.75 p-3.75 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between pb-3">
               <div id={titleId} className="text-l-xl font-bold">Filter</div>
               <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-700 font-bold">
                  <Close16Icon className="text-black w-4 h-4" />
               </button>
            </div>

            {/* Dropdowns */}
            <div className="space-y-3">
               {/* Date range picker */}
               <div className="w-full flex">
                  <Popover
                     open={openCalendar}
                     onOpenChange={active => {
                        if (!active) {
                           setSelectedMonth(dateFilter.length !== 0 ? moment(dateFilter[0]) : moment());
                        }

                        setOpenCalendar(active);
                     }}
                     content={
                        <div>
                           <div className="flex justify-between">
                              <button
                                 className="active:bg-border hover:bg-border min-w-6 min-h-6 flex items-center justify-center rounded"
                                 onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    setSelectedMonth(preValue => preValue.clone().subtract(1, 'month'));
                                 }}
                              >
                                 <HiOutlineChevronLeft size={18} color="black" />
                              </button>
                              <span className="text-black font-bold text-sm">{selectedMonth.format('MMMM YYYY')}</span>
                              <button
                                 className="active:bg-border hover:bg-border min-w-6 min-h-6 flex items-center justify-center rounded disabled:opacity-50 disabled:bg-border"
                                 disabled={selectedMonth.isAfter(moment().add(10, 'year'))}
                                 onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    setSelectedMonth(preValue => preValue.clone().add(1, 'month'));
                                 }}
                              >
                                 <HiOutlineChevronRight size={18} color="black" />
                              </button>
                           </div>
                           <div className="mt-3">
                              <Calendar
                                 disabledBefore={moment().subtract(10, 'year')}
                                 range={dateFilter}
                                 selectedMonth={selectedMonth}
                                 onChange={(value?: Moment) => {
                                    if (!value) return;

                                    if (dateFilter.length % 2 === 0) {
                                       setDateFilter([value]);
                                       return;
                                    }

                                    const [firstDate] = dateFilter;
                                    const fromDate = moment.min([firstDate as Moment, value]);
                                    const toDate = moment.max([firstDate as Moment, value]);

                                    setDateFilter([fromDate, toDate]);
                                    setFilters(p => ({
                                       ...p,
                                       fromDate: fromDate.format(DATE_FORMAT.SERVER_DATE),
                                       toDate: toDate.format(DATE_FORMAT.SERVER_DATE),
                                    }));
                                    setOpenCalendar(false);
                                 }}
                                 isOnAdminSite
                              />
                           </div>
                        </div>
                     }
                     theme={{
                        base: 'w-[calc(100vw-30px)] !left-1.75 p-3.75 sm:max-w-[346px]',
                        content: 'z-10 overflow-hidden rounded-none',
                     }}
                  >
                     <InputField
                        acceptChars={/[0-9/]/}
                        clearable
                        placeholder={t('activityLogs.dateRange')}
                        endIcon={<HiOutlineCalendar size={16} color="gray" />}
                        onEndIconClick={() => setOpenCalendar(!openCalendar)}
                        theme={{ border: 'border-gray-200 max-h-8.5', input: 'min-h-8', endIcon: 'w-6.5 min-w-6.5 h-6.5 min-h-6.5' }}
                        value={
                           dateFilter?.length === 2
                              ? `${moment(dateFilter?.[0]).format(DATE_FORMAT.STANDARD)} - ${moment(dateFilter?.[1]).format(DATE_FORMAT.STANDARD)}`
                              : ''
                        }
                        onClear={() => {
                           setDateFilter([]);
                           setFilters(preValues => {
                              return {
                                 ...preValues,
                                 fromDate: undefined,
                                 toDate: undefined,
                              };
                           });
                        }}
                     />
                  </Popover>
               </div>
            </div>

            {/* Toggle buttons — site filter */}
            <div className="h-8.5 bg-border rounded-6 p-0.5 gap-0.5 flex justify-center items-center">
               <button
                  disabled={isFetching}
                  onClick={() =>
                     setFilters(preValues => ({
                        ...preValues,
                        page: 0,
                        isFetchAllSites: false,
                     }))
                  }
                  className={`h-full flex-1 text-sm font-semibold rounded-md px-3.75 text-nowrap ${!filters?.isFetchAllSites ? 'bg-primary text-white rounded-6 border' : ''}`}
               >
                  This Site
               </button>
               <button
                  disabled={isFetching}
                  onClick={() =>
                     setFilters(preValues => ({
                        ...preValues,
                        page: 0,
                        isFetchAllSites: true,
                     }))
                  }
                  className={`h-full flex-1 text-sm font-semibold rounded-md px-3.75 text-nowrap ${filters?.isFetchAllSites ? 'bg-primary text-white rounded-6 border' : ''}`}
               >
                  All Sites
               </button>
            </div>
         </div>
      </SlideUpModal>
   );
};

export default FilterModal;
